/* eslint-disable react/no-this-in-sfc */
import React, {
  memo,
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import classnames from 'classnames';
import { Select, NoDataWithName } from '@components';
import { produce, setAutoFreeze } from 'immer';
import D_Number from '@utils/number';
import D_Currency from '@utils/currency';
import useTheme from '@hooks/useTheme';
import { cloneDeep } from 'lodash';
import { condition } from './index';
import clear_filter_icon from '@assets/imgs/icons/clear_filter.png';

const resizeChartByLegendHeight = (chart) => {
  if (
    chart.chartHeight !== chart.legend.legendHeight + 370 &&
    chart.legend.legendHeight
  ) {
    chart.setSize(null, 370 + chart.legend.legendHeight);
  }
};

setAutoFreeze(false);
function TotalRevenueChart(props, ref) {
  // metrics: {{buyer{eps,grossMargin,...}},{target}}
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  const {
    tabKey,
    data = [],
    metrics = {},
    handleSelectYear,
    expand,
    timeline,
    isTabChanging,
    names,
  } = props;
  const filterImageRef = useRef({});
  const [chartOptions, setChartOptions] = useState([]);
  const [selectTimelineValue, setSelectTimelineValue] = useState(timeline);
  const [selectMetricsValue, setSelectMetricsValue] = useState('default');
  const [chartCondition, setChartCondition] = useState(condition);
  const chartRefs = [useRef(null), useRef(null)];
  const [isBothNodata, setIsBothNoData] = useState(false);
  const [syncTimes, setSyncTimes] = useState(0);
  const [yAxisMax, setYAxisMax] = useState({
    leftMax: 0,
    leftMin: 0,
    rightMax: 0,
    rightMin: 0,
  });

  const showHideFilterIcon = (chart, index, event) => {
    if (!chart) {
      return;
    }
    const legendItems = cloneDeep(chart.legend.allItems);
    if (legendItems && legendItems.length) {
      let hasHide = false;
      for (let i = 0; i < legendItems.length; i++) {
        if (event && legendItems[i].name === event.target.name && !hasHide) {
          hasHide = legendItems[i].visible;
        } else if (!legendItems[i].visible && !hasHide) {
          hasHide = true;
        }
      }
      if (hasHide) {
        filterImageRef.current &&
          filterImageRef.current[index] &&
          filterImageRef.current[index].show();
      } else {
        filterImageRef.current &&
          filterImageRef.current[index] &&
          filterImageRef.current[index].hide();
      }
    }
  };

  const formatOption = function (option, index) {
    if (option.isManual) {
      return { isManual: true };
    }
    const formattedOption = produce(option, (draft) => {
      if (!draft) {
        return;
      }
      draft.yAxis[0].labels.formatter = function () {
        const _this = this;
        return D_Currency.format(
          {
            value: _this.value,
            currencyIsoCode: _this.chart.options.currencyIsoCode.revenue,
          },
          1,
        );
      };
      const tootipPrefix = function (that) {
        return (
          `<b>${that.point.name}</b>` +
          `<br>` +
          `<b>${that.series.name}</b>` +
          `: `
        );
      };

      const percentOfTotalPrefix = function (that) {
        if (that.y === that.total) {
          return '';
        }

        return `<br/><b>% of Total:</b>${D_Number.formatPercentage(
          that.y / that.total,
          1,
        )}`;
      };
      draft.tooltip.formatter = function () {
        const _this = this;
        switch (_this.series.name) {
          case 'Sales Growth':
          case 'Gross Margin':
            return tootipPrefix(_this) + D_Number.formatPercentage(_this.y, 1);
          case 'Market Cap':
          case 'EPS':
          default:
            return (
              tootipPrefix(_this) +
              D_Currency.format({
                value: _this.y,
                currencyIsoCode: _this.point.currencyIsoCode,
              }) +
              percentOfTotalPrefix(_this)
            );
        }
      };
      // 1Y by Quarter/ LTM by Quarter should have 2 lines on X axis
      // draft.legend.x = 28;

      draft.legend.title = {
        text: draft.xAxis.title.text
          ? `<span style="font-size: 0.875rem; color: #666; font-weight: normal">${draft.xAxis.title.text}</span>`
          : '',
        style: {
          fontStyle: 'normal',
        },
      };

      draft.xAxis.title.text = '';

      draft.plotOptions = {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: false,
          },
        },
        series: {
          events: {
            legendItemClick(event) {
              showHideFilterIcon(this.chart, index, event);
            },
          },
        },
      };

      draft.chart.events = {
        load() {
          resizeChartByLegendHeight(this);
        },
        redraw() {
          resizeChartByLegendHeight(this);
        },
        render() {
          const legend = this.legend;
          const title = legend.title;
          if (title) {
            const legendWidth = legend.legendWidth;
            const titleWidth = title.width;
            const diff = legendWidth - titleWidth;
            let offsetX = diff / 2;
            if (titleWidth >= legendWidth - 8) {
              offsetX -= diff;
            }
            title.attr({ x: offsetX });
          }
        },
      };
    });

    return formattedOption;
  };
  const buildMetricsSeriesData = (optionIndex, type) =>
    metrics[optionIndex][type]?.map((m) => {
      // console.log(m);
      if (m) {
        return {
          name: m[0].tick,
          y: m[1],
          currencyIsoCode: m[0].currencyIsoCode || null,
        };
      }
      return null;
    });

  // set line in chart
  const drawLine = (type) => {
    syncYAxis([0, 0], [0, 0], [0, 0], [0, 0]);
    setSyncTimes(0);
    const formattedOptions = chartOptions.map((option, i) => {
      if (option.isManual) {
        return option;
      }
      return produce(option, (draft) => {
        draft.series = draft.series?.filter((series) => series.type !== 'line');
        if (type === 'default') {
          draft.yAxis[1].visible = false;
          return;
        }
        if (!isLightMode) {
          draft.plotOptions.column.dataLabels = { color: '#ACACAC' };
        }
        if (metrics[i][type].find((m) => m != null && m !== '')) {
          draft.yAxis[1].softMax = 0;
          draft.yAxis[1].softMin = 0;
          draft.series.push({
            name: condition.Metrics.find((x) => x.value === type).name,
            type: 'line',
            data: buildMetricsSeriesData(i, type),
            yAxis: 1,
            borderWidth: 0,
            marker: {
              symbol: 'circle',
            },
          });
          draft.yAxis[1].labels.formatter = function () {
            const _this = this;
            if (type === 'eps' || type === 'marketCap') {
              return D_Currency.format(
                {
                  value: _this.value,
                  currencyIsoCode: _this.chart.options.currencyIsoCode[type],
                },
                0,
              );
            }
            return `${D_Number.formatPercentage(_this.value, 0)}`;
          };
          draft.yAxis[1].visible = true;
        }
      });
    });
    setChartOptions(formattedOptions);
    reflowChart(chartRefs);
    setSelectMetricsValue(type);
  };

  useImperativeHandle(ref, () => ({
    drawLine: (type) => drawLine(type),
  }));

  const handleConditionMetrics = (time) => {
    if (time === 'LTMQ' || time === 'Y1Q') {
      setChartCondition({
        Timeline: condition.Timeline,
        Metrics: condition.Metrics.filter((m) => m.value !== 'eps'),
      });
    } else {
      setChartCondition(condition);
    }
  };
  const selectTime = (time) => {
    if (time !== selectTimelineValue) {
      setSyncTimes(0);
      setSelectTimelineValue(time);
      handleSelectYear(time);
      handleConditionMetrics(time);
      drawLine('default');
      showHideFilterIcon(chartRefs[0].current.chart, 0, null);
      showHideFilterIcon(chartRefs[1].current.chart, 1, null);
    }
  };

  useEffect(() => {
    setIsBothNoData(data.filter((option) => option.isManual).length === 2);
    setChartOptions([formatOption(data[0], 0), formatOption(data[1], 1)]);
    handleConditionMetrics(timeline);
    // selectTime(selectTimelineValue);
  }, [data]);

  const reflowChart = (chartsRef) => {
    try {
      chartsRef &&
        chartsRef.forEach((chart) => {
          chart.current &&
            chart.current.chart.reflow &&
            chart.current.chart.reflow() &&
            chart.current.chart.redraw();
        });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setSelectTimelineValue(timeline);
    reflowChart(chartRefs);
    // console.log(expand);
  }, [expand, isTabChanging]);

  const syncYAxis = (
    leftYAxisMaxData,
    rightYAxisMaxData,
    leftYAxisMinData,
    rightYAxisMinData,
  ) => {
    const leftMax = D_Number.getArrayMaxNumber(leftYAxisMaxData);
    const rightMax = D_Number.getArrayMaxNumber(rightYAxisMaxData);
    const leftMin = D_Number.getArrayMinNumber(leftYAxisMinData);
    const rightMin = D_Number.getArrayMinNumber(rightYAxisMinData);
    // console.log({ leftMax, rightMax, leftMin, rightMin });
    if (
      yAxisMax.leftMax !== leftMax ||
      yAxisMax.rightMax !== rightMax ||
      yAxisMax.leftMin !== leftMin ||
      yAxisMax.rightMin !== rightMin
    ) {
      if (syncTimes < 4) {
        setSyncTimes(syncTimes + 1);
        setYAxisMax({ leftMax, rightMax, leftMin, rightMin });
      }
    }
  };
  useEffect(() => {
    // debugger;
    if (chartRefs[0].current?.chart || chartRefs[1].current?.chart) {
      const leftYAxisMaxs = chartRefs.map(
        (c) => c.current?.chart?.yAxis[0]?.max,
      );
      const rightYAxisMaxs = chartRefs.map(
        (c) => c.current?.chart?.yAxis[1]?.max,
      );
      const leftYAxisMins = chartRefs.map(
        (c) => c.current?.chart?.yAxis[0]?.min,
      );
      const rightYAxisMins = chartRefs.map(
        (c) => c.current?.chart?.yAxis[1]?.min,
      );
      syncYAxis(leftYAxisMaxs, rightYAxisMaxs, leftYAxisMins, rightYAxisMins);
    }
  }, [chartRefs, selectMetricsValue, timeline, data, yAxisMax]);
  // sync yaxis
  useEffect(() => {
    // console.log(yAxisMax);
    if (
      yAxisMax &&
      (yAxisMax.leftMax !== 0 ||
        yAxisMax.rightMax !== 0 ||
        yAxisMax.leftMin !== 0 ||
        yAxisMax.rightMin !== 0)
    ) {
      const yAxisSyncedOptions = chartOptions.map((option) =>
        produce(option, (draft) => {
          if (draft && draft.yAxis) {
            draft.yAxis[0].softMax = yAxisMax.leftMax;
            draft.yAxis[0].softMin = yAxisMax.leftMin;
            draft.yAxis[1].softMax = yAxisMax.rightMax;
            draft.yAxis[1].softMin = yAxisMax.rightMin;
            if (yAxisMax.leftMax !== 0) {
              const suffix = D_Number.getProperSuffix(yAxisMax.leftMax);
              draft.yAxis[0].labels.formatter = function () {
                const _this = this;
                return D_Currency.formatWithCertainSuffix(
                  _this.value,
                  suffix,
                  1,
                  _this.chart.options.currencyIsoCode.revenue,
                );
              };
            }
            if (yAxisMax.rightMax !== 0) {
              const suffix = D_Number.getProperSuffix(yAxisMax.rightMax);
              draft.yAxis[1].labels.formatter = function () {
                const _this = this;
                if (
                  selectMetricsValue === 'eps' ||
                  selectMetricsValue === 'marketCap'
                ) {
                  return D_Currency.formatWithCertainSuffix(
                    _this.value,
                    suffix,
                    1,
                    _this.chart.options.currencyIsoCode[selectMetricsValue],
                  );
                }
                return `${D_Number.formatPercentage(_this.value, 0)}`;
              };
            }
          }
        }),
      );
      setChartOptions(yAxisSyncedOptions);
      // reflowChart(chartRefs);
    }
  }, [yAxisMax, data]);

  /* 用chart的series可以判断是否有了filter - wudi */
  // console.log('aaa: ', chartRefs[0]?.current?.chart);
  // console.log('aaa: ', chartRefs[1]?.current?.chart);
  const clearFilters = (i) => {
    try {
      const chart = chartRefs[i].current.chart;
      chart.series.forEach((series) => {
        series.setVisible(true);
      });
      filterImageRef.current &&
        filterImageRef.current[i] &&
        filterImageRef.current[i].hide();
    } catch (_unused) {
      console.log('not have chart');
    }
  };

  const highchartsCallback = (chart, i) => {
    filterImageRef.current[i] = chart.renderer
      .image(clear_filter_icon, 15, chart.chartHeight - 25 - 14, 14, 14)
      .on('click', () => {
        clearFilters(i);
      })
      .on('mouseover', function (e) {
        const tooltipDom = document.querySelector(
          `.chart-content-${i + 1}${tabKey ? `-${tabKey}` : ''}`,
        ).firstElementChild;
        if (!tooltipDom.classList.contains('tooltip-visibility')) {
          const chartOffsetLeft =
            (chart.container && chart.container.offsetLeft) || 0;
          tooltipDom.classList.add('tooltip-visibility');
          tooltipDom.style.top = `${(e.layerY - 50) / 16}rem`;
          tooltipDom.style.left = `${
            (e.layerX - 38 + chartOffsetLeft) / 16
          }rem`;
        }
      })
      .on('mouseout', () => {
        document
          .querySelector(`.chart-content-${i + 1}${tabKey ? `-${tabKey}` : ''}`)
          .firstElementChild.classList.remove('tooltip-visibility');
      })
      .css({
        width: '0.875rem',
        height: '0.875rem',
        cursor: 'pointer',
      })
      .add();
    filterImageRef.current &&
      filterImageRef.current[i] &&
      filterImageRef.current[i].hide();
  };

  return (
    <div className="highchart-container total-revenue">
      {!isBothNodata ? (
        <div className="conditionContainer">
          <div className="conditionList">
            <div className="conditionSelection">
              <label>Timeline</label>
              <Select
                size="sm"
                style={{ width: 130 }}
                onSelect={selectTime}
                value={selectTimelineValue}
                options={chartCondition.Timeline}
              />
            </div>
            <div className="conditionSelection">
              <label>Metrics</label>
              <Select
                size="sm"
                style={{ width: 130 }}
                onSelect={drawLine}
                value={selectMetricsValue}
                options={chartCondition.Metrics}
              />
            </div>
          </div>
        </div>
      ) : null}
      {chartOptions &&
        chartOptions.map((option, i, optionsArr) => {
          const isLast = optionsArr.length - 1 === i;
          if (!option.isManual) {
            return (
              <>
                <div
                  key={`key-${i}`}
                  className={classnames(
                    'chart-content',
                    `chart-content-${i + 1}${tabKey ? `-${tabKey}` : ''}`,
                    'clear-filter-tooltip',
                    {
                      'highcharts-dark': !isLightMode,
                      'highcharts-light': isLightMode,
                    },
                  )}
                >
                  {/* <div
                    className="clear_filter_btn"
                    style={{ position: "absolute", left: clearFilterIconPoint.x, top: clearFilterIconPoint.y }}
                    onClick={() => {
                      clearFilters(i)
                    }}
                  >
                    <Tooltip 
                      title="Clear Filter"
                      getTooltipContainer={trigger => trigger.parentElement}
                    >
                      <img
                        width={14}
                        src={clear_filter_icon}
                        alt="clear filter icon"
                      />
                    </Tooltip>
                    Clear Filter
                  </div> */}
                  <p className="tooltip-text">Clear Filter</p>
                  <HighchartsReact
                    key={`key-${i}`}
                    highcharts={Highcharts}
                    options={option}
                    ref={chartRefs[i]}
                    callback={(c) => highchartsCallback(c, i)}
                  />
                </div>
                {!isLast && <div className="line" />}
              </>
            );
          }
          return (
            <>
              <NoDataWithName
                key={`key-${i}`}
                name={i === 0 ? names.buyerName : names.targetName}
              />
              {!isLast && <div className="line" />}
            </>
          );
        })}
    </div>
  );
}

// TotalRevenueChart.propTypes = { data: PropTypes.object };

export default memo(forwardRef(TotalRevenueChart));
